import React, { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";

import { PlusIcon } from "@heroicons/react/24/solid";
import Modal from "../../components/common/Modal";
import PageHeading from "../../components/common/PageHeading";
import Button from "../../components/form/Button";
import CustomSelect, { OptionType } from "../../components/form/CustomSelect";
import { useGetAccountTagsQuery } from "../../redux/services/accountTags";
import { useUpdateTemplateMutation } from "../../redux/services/templates";
import CreateAccountTagModal from "./AccountTagModal";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  accountId: string;
  templateId: string;
  initialTagId?: string | null;
};

export default function AttachAccountTagModal({
  isOpen,
  onClose,
  accountId,
  templateId,
  initialTagId = null,
}: Props) {
  const {
    data: tags,
    isFetching,
    refetch: refetchTags,
  } = useGetAccountTagsQuery({ accountId }, { skip: !accountId });

  const [updateTemplate, { isLoading: isSaving }] = useUpdateTemplateMutation();

  const [selectedTag, setSelectedTag] = useState<OptionType | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const tagOptions: OptionType[] = useMemo(
    () =>
      tags?.data?.map((tag: any) => ({
        value: tag.accountTagId,
        label: tag.name,
        description: tag.description || "",
      })) ?? [],
    [tags?.data]
  );

  useEffect(() => {
    if (isOpen && tags?.data) {
      const found = tagOptions.find((t) => t.value === initialTagId);
      setSelectedTag(found || null);
    }
  }, [isOpen, initialTagId, tags?.data, tagOptions]);

  const handleSave = async () => {
    try {
      await updateTemplate({
        templateId,
        accountId,
        accountTagId: selectedTag?.value ?? null,
      }).unwrap();
      toast.success("Tag updated successfully.");
      onClose();
    } catch (err) {
      toast.error("Failed to update tag.");
    }
  };

  return (
    <>
      <Modal
        isOpen={true}
        onRequestClose={onClose}
        width={500}
      >
        <div className="p-6 space-y-4">
          <PageHeading heading="Attach a Tag to Article Template Settings" />

          <CustomSelect
            label=""
            placeholder="Select a tag"
            options={tagOptions}
            selected={selectedTag}
            setSelected={setSelectedTag}
            disabled={isFetching}
            showDescriptionBelowLabel
          />

          <div
            className="flex items-center gap-1 text-2xs text-primary cursor-pointer uppercase font-semibold tracking-wide"
            onClick={() => setIsCreateModalOpen(true)}
          >
            <PlusIcon className="w-3.5 h-3.5" /> Create New Tag
          </div>

          <div className="flex gap-3 pt-2">
            <Button
              variant="primary"
              onClick={handleSave}
              loading={isSaving}
              className="min-w-24"
            >
              Save
            </Button>
            <Button
              variant="transparent"
              onClick={onClose}
            >
              Cancel
            </Button>
          </div>
        </div>
      </Modal>

      {isCreateModalOpen && (
        <CreateAccountTagModal
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          accountId={accountId}
          onSuccess={() => {
            refetchTags();
            setIsCreateModalOpen(false);
          }}
        />
      )}
    </>
  );
}
